/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { FeatureFlags, Project } from '@/types/database'
import { useProject } from './useProjects'

export function useFeatureFlags(projectId: string | undefined) {
  const { data: project } = useProject(projectId)

  const flags: FeatureFlags = {
    enable_clip_thumbnails: project?.custom_fields?.feature_flags?.enable_clip_thumbnails ?? false,
    enable_vfx_shot_thumbnails: project?.custom_fields?.feature_flags?.enable_vfx_shot_thumbnails ?? false,
    enable_email_notifications: project?.custom_fields?.feature_flags?.enable_email_notifications ?? false,
  }

  return {
    flags,
    isLoading: !project,
  }
}

export function useUpdateFeatureFlags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ projectId, flags }: { projectId: string; flags: Partial<FeatureFlags> }) => {
      const { data: project, error: fetchError } = await supabase
        .from('projects')
        .select('custom_fields')
        .eq('id', projectId)
        .maybeSingle()

      if (fetchError) throw fetchError
      if (!project) throw new Error('Project not found')

      const updatedCustomFields = {
        ...project.custom_fields,
        feature_flags: {
          ...project.custom_fields?.feature_flags,
          ...flags,
        },
      }

      const { data, error } = await supabase
        .from('projects')
        .update({ custom_fields: updatedCustomFields })
        .eq('id', projectId)
        .select()
        .maybeSingle()

      if (error) throw error
      if (!data) throw new Error('Failed to update feature flags')

      return data as Project
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects', data.id] })
    },
  })
}
